import type Widget from "@/model/widget";
import WidgetCard from "./widget-card";
import { Bell, CircleArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNotification } from "@/hooks/use-notification";
import useUserInfo from "@/hooks/use-user-info";
import { NoDataAvailable } from "@/pages/components/empty";
import { WidgetId } from "@/constants";
import { Skeleton } from "@/components/ui/skeleton";
import NotificationItem from "../notification/notification-item";
import { useNavigate } from "react-router-dom";
import ScreenRouteNames from "@/constants/screen-route-names";
import { useState } from "react";
import RightSidebar from "../right-sidebar";
import NotificationDetail from "../notification/notification-detail";
import type { Notification } from "@/model/notification";
import { htmlFromString } from "@/lib/string-util";

type ICampaignWidgetProps = {
  className?: string;
  widget: Widget;
};

const CampaignWidget: React.FunctionComponent<ICampaignWidgetProps> = ({
  widget,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] =
    useState<Notification | null>(null);

  const navigate = useNavigate();
  const userInfo = useUserInfo();
  const { data: notifications, isFetching: isNotificationFetching } =
    useNotification(userInfo);

  const onSelectNotification = (notification: Notification) => {
    setSelectedNotification(notification);
    setOpen(true);
  };

  const viewAllNotifications = () => {
    const targetPath = `/${ScreenRouteNames.NOTIFICATION.path}`;
    navigate(targetPath, {
      replace: false,
    });
  };

  const renderWidget = () => {
    if (isNotificationFetching) {
      return (
        <Card>
          <CardContent className="m-4 flex size-full flex-col items-center justify-center gap-2">
            <Skeleton className="h-4 w-full bg-gray-200" />
            <Skeleton className="h-4 w-full bg-gray-200" />
            <Skeleton className="h-4 w-full bg-gray-200" />
          </CardContent>
        </Card>
      );
    } else {
      if (notifications && notifications.length > 0) {
        const recentNotifications =
          widget.widgetId === WidgetId.SingleNotification
            ? notifications.slice(0, 1)
            : notifications.slice(0, 4);

        return recentNotifications?.map((item, index) => {
          return (
            <NotificationItem
              key={`${item?.messageId}_${index}`}
              notification={item}
              onSelect={onSelectNotification}
            />
          );
        });
      } else {
        return (
          <div className="flex size-full items-center justify-center">
            <NoDataAvailable
              title=""
              message="No new notifications."
              icon={Bell}
            />
          </div>
        );
      }
    }
  };

  const footerContent = (
    <Button
      variant="link"
      className="w-full hover:underline"
      onClick={viewAllNotifications}
    >
      View All Notification(s) <CircleArrowRight className="ml-1" />
    </Button>
  );

  return (
    <>
      <WidgetCard
        title={widget?.widgetDisplayName}
        footerContent={footerContent}
      >
        {renderWidget()}
      </WidgetCard>
      <RightSidebar
        title={`${selectedNotification?.subject ? htmlFromString(selectedNotification?.subject) : ""}`}
        isOpen={open}
        onClose={() => setOpen(false)}
      >
        {selectedNotification && (
          <NotificationDetail
            notification={selectedNotification}
            onClose={() => setOpen(false)}
          />
        )}
      </RightSidebar>
    </>
  );
};

export default CampaignWidget;
